import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, message } from 'antd';
import { submitNonStdRound } from '../../services/nonstandardOps';
import { NonStdEntry } from '../../types/nonstandardOps';

interface Props {
  open: boolean;
  instructionId: number;
  round: number;
  /** 本轮员工记录的条目 */
  entries: NonStdEntry[];
  onClose: () => void;
  onSubmitted: () => void;
}

/**
 * 提交本轮结果，请客户反馈：在时间线上加一条"本轮结束"的里程碑，指令进入"等待客户反馈"。
 * 客户能看到哪些记录由每条记录自己的"对客户可见"开关决定；这里只提醒本轮有几条可见，没有可见记录就不能提交。
 * 不结束执行阶段——提交后员工可以继续记录，客户回复后再处理下一轮。
 */
const SubmitRoundModal: React.FC<Props> = ({ open, instructionId, round, entries, onClose, onSubmitted }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const visibleCount = entries.filter(e => e.customer_visible).length;

  useEffect(() => {
    if (open) setText('');
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await submitNonStdRound(instructionId, { text: text.trim() || undefined });
      message.success(`第 ${round} 轮结果已提交，请客户反馈`);
      onSubmitted();
    } catch (err: any) {
      const data = err?.response?.data;
      message.error(data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || '提交失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`提交第 ${round} 轮结果，请客户反馈`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="提交，请客户反馈"
      cancelText="取消"
      okButtonProps={{ disabled: visibleCount === 0 }}
      confirmLoading={saving}
      width={560}
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        本轮共 {entries.length} 条记录，其中 <b>{visibleCount}</b> 条对客户可见。
      </div>
      {visibleCount === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="本轮没有对客户可见的记录，客户看不到内容就没法反馈。请先把要给客户看的记录设为“对客户可见”（记录旁边的链接或开关）。"
        />
      ) : null}
      <Input.TextArea
        rows={3}
        maxLength={2000}
        placeholder={`给客户的话（选填），例如：已扫描 52 个 LPN，清单见附件，请回复每个 LPN 的处理方式。不填则显示"第 ${round} 轮结果已提交，请查看并反馈。"`}
        value={text}
        onChange={e => setText(e.target.value)}
      />
      <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
        提交不会结束执行阶段，之后您仍然可以继续记录；客户回复后再处理下一轮。
      </div>
    </Modal>
  );
};

export default SubmitRoundModal;
